import { useEffect, useRef, useState } from "react";

interface ColorInputProps {
    color: string;
    onChange: (color: string) => void;
    index: number;
}

export function ColorInput({ color, onChange, index }: ColorInputProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [textValue, setTextValue] = useState(color);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setTextValue(color);
    }, [color]);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        [],
    );

    const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextColor = e.target.value;
        setTextValue(nextColor);
        scheduleCommit(nextColor);
    };

    const scheduleCommit = (value: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            onChange(value);
        }, 150);
    };

    const handleClick = () => {
        inputRef.current?.click();
    };

    return (
        <div className="relative flex items-center gap-2 rounded-lg border border-[#d3e2e8] bg-white px-2 cursor-pointer hover:border-[#079a95] transition">
            <button
                type="button"
                onClick={handleClick}
                className="w-4 h-4 rounded flex-shrink-0 border border-[#d3e2e8] hover:border-[#079a95] transition"
                style={{ backgroundColor: textValue }}
                title="Click to change color"
            />
            <input
                ref={inputRef}
                type="color"
                value={color}
                onChange={handleColorInput}
                className="absolute opacity-0 w-0 h-0 pointer-events-none"
                aria-label={`Color ${index + 1}`}
            />
            <input
                type="text"
                value={textValue}
                onChange={(e) => {
                    setTextValue(e.target.value);
                }}
                onBlur={() => {
                    // Clear any pending debounce
                    if (debounceRef.current) {
                        clearTimeout(debounceRef.current);
                        debounceRef.current = null;
                    }

                    const normalized = normalizeHex(textValue, color);
                    setTextValue(normalized);
                    if (normalized !== color) {
                        onChange(normalized);
                    }
                }}
                className="text-[12px] font-medium text-[#2d3a3f] bg-transparent border-none ring-0 flex-1 min-w-0 px-0 py-1 outline-none"
                placeholder="#000000"
                maxLength={7}
            />
        </div>
    );
}

const normalizeHex = (value: string, fallback: string) => {
    if (!value) return fallback;
    const val = value.startsWith("#") ? value : `#${value}`;
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
        return val;
    }
    return fallback;
};
